<template>
    <v-menu offset-y transition="slide-y-transition" bottom :close-on-content-click="false" v-model="chatOpen" min-width="600" max-width="800">
        <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
                <v-icon>mdi-chat</v-icon>
                <v-badge v-if="unreadCount > 0" :content="unreadCount" color="red" overlap bordered style="position: absolute; top: 0; right: 0"></v-badge>
            </v-btn>
        </template>
        <v-card>
            <v-card-title class="py-2 px-4">
                Quaro KI
                <v-spacer />
                <v-btn icon small @click="chatOpen = false"><v-icon>mdi-close</v-icon></v-btn>
            </v-card-title>
            <v-divider />
            <v-card-text style="max-height: 300px; overflow-y: auto; background: #f9f9f9">
                <div v-if="messages.length === 0" class="grey--text text--darken-1 text-center py-5">No messages yet.</div>
                <div v-for="(msg, i) in messages" :key="i" class="mb-3 d-flex" :class="msg.user === 'KI' ? 'chat-row-left' : 'chat-row-right'">
                    <template v-if="msg.user === 'KI'">
                        <v-avatar height="32px" width="32px" min-width="32px" class="comment-avat white--text mr-2" style="background: #009f89">KI</v-avatar>
                        <div class="comment-area ki-message">
                            <v-list-item-title>
                                <span class="comment-user">KI</span>
                                <span class="comment-date">{{ formatDate(msg.date) }}</span>
                            </v-list-item-title>
                            <div class="comment-text" v-html="renderMarkdown(msg.text)"></div>

                            <!-- Tabellenblock jetzt als Button -->
                            <div v-if="msg.tables && msg.tables.length" class="mt-2">
                                <div v-for="(table, tIdx) in msg.tables" :key="tIdx" class="mb-4">
                                    <div class="font-weight-bold mb-2" style="color: #1976d2">Source: {{ table.name }}</div>
                                    <v-btn small color="primary" @click="showTable(table)" :loading="loadingTable[JSON.stringify(table.rawOutput)]"> Visualize </v-btn>
                                </div>
                            </div>
                        </div>
                    </template>
                    <template v-else>
                        <div class="comment-area user-message">
                            <v-list-item-title class="justify-end">
                                <span class="comment-user">{{ msg.user }}</span>
                                <span class="comment-date">{{ formatDate(msg.date) }}</span>
                            </v-list-item-title>
                            <div class="comment-text">{{ msg.text }}</div>
                        </div>
                        <v-avatar
                            height="32px"
                            width="32px"
                            min-width="32px"
                            class="comment-avat white--text ml-2"
                            :style="{ backgroundColor: $helpers ? $helpers.stringToBackgroundColor(msg.user) : '#1976d2' }"
                            >{{ $helpers ? $helpers.getInitials(msg.user) : msg.user.substr(0, 2).toUpperCase() }}</v-avatar
                        >
                    </template>
                </div>
                <!-- Ladeanzeige -->
                <div v-if="loading" class="mb-3 d-flex">
                    <v-avatar height="32px" width="32px" min-width="32px" class="comment-avat white--text mr-2" style="background: #009f89"> KI </v-avatar>
                    <div class="comment-area">
                        <v-list-item-title>
                            <span class="comment-user">KI</span>
                            <span class="comment-date">{{ formatDate(new Date()) }}</span>
                        </v-list-item-title>
                        <div class="comment-text">
                            <v-progress-circular indeterminate size="20" width="2" color="primary" style="vertical-align: middle; margin-right: 8px" />
                            generating response...
                            <div v-if="slowHint" class="grey--text text--darken-1" style="font-size: 12px; margin-top: 4px">
                                Working on it — analyses across multiple data sources can take up to two minutes.
                            </div>
                        </div>
                    </div>
                </div>
                <KITableDialog :value.sync="tableDialog" :table="selectedTable" @input="onTableDialogInput" />
            </v-card-text>
            <v-divider />
            <v-card-actions class="py-2 px-3">
                <v-text-field v-model="input" dense hide-details rounded placeholder="Ask me anything about SEO..." @keyup.enter="send" style="flex: 1" />
                <v-btn icon color="primary" @click="send" :disabled="!input || loading">
                    <v-icon>mdi-send</v-icon>
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-menu>
</template>

<script>
    import MarkdownIt from "markdown-it"
    import KITableDialog from "./KITableDialog.vue"
    export default {
        name: "KIChat",
        components: { KITableDialog },
        data() {
            return {
                chatOpen: false,
                input: "",
                messages: [],
                unreadCount: 0,
                loading: false,
                tableVisible: {}, // <--- Sichtbarkeit pro Tabelle
                tableDialog: false,
                selectedTable: null,
                loadingTable: {}, // statt loadingTable: false
                tableDefCache: {}, // <--- Cache für Tabellen-Definitionen
                // Hinweis bei langen Analysen (mehrere Tool-Quellen) - siehe send()
                slowHint: false,
                slowHintTimer: null,
                // Teil 10: laufender Chat-Job (async, ueber Socket + /v2/sources/chat-job)
                pendingJob: null,
                chatWatchdogTimer: null
            }
        },
        watch: {
            chatOpen(val) {
                if (val) this.unreadCount = 0
            }
        },
        mounted() {
            // Teil 10: AI-Chat laeuft asynchron ueber einen Server-Job (Socket-Signal + kurzer
            // Status-Abruf) statt eines blockierenden POST - siehe searchinsights-api chatJobController.js.
            this.$socket.on("chatJobUpdate", this.onChatJobUpdate)
        },
        beforeDestroy() {
            this.$socket.off("chatJobUpdate", this.onChatJobUpdate)
            if (this.chatWatchdogTimer) clearTimeout(this.chatWatchdogTimer)
        },
        methods: {
            renderMarkdown(text) {
                const md = new MarkdownIt()

                return md.render(text || "")
            },
            async send() {
                if (!this.input || this.loading) return
                // Eigene Nachricht anzeigen
                const userInput = this.input
                this.messages.push({
                    user: this.getUsername(),
                    text: userInput,
                    date: new Date()
                })
                this.input = ""
                this.loading = true
                // Analysen über mehrere Tool-Quellen (Quaro + DataForSEO) können deutlich länger dauern als ein
                // Ein-Tool-Lookup - nach 15s ein Hinweis, damit "generating response..." nicht wie ein Hänger wirkt.
                this.slowHint = false
                this.slowHintTimer = setTimeout(() => {
                    this.slowHint = true
                }, 15000)
                this.$nextTick(() => {
                    const el = this.$el.querySelector(".v-card-text")
                    if (el) el.scrollTop = el.scrollHeight
                })
                // Teil 10: der Chat-Request selbst dauert jetzt nur noch die Job-Erstellung (kurz) - die
                // eigentliche Antwort kommt per Socket-Signal + Abruf, siehe onChatJobUpdate/pollPendingChatJob.
                const clientJobId = this.generateClientJobId()
                try {
                    const res = await this.$helpers.axiosPost(
                        process.env.VUE_APP_APIURL + "/v2/sources/chat",
                        { user_id: localStorage.id, company_id: localStorage.company_id, question: userInput, socket_id: this.$socket.id || null, client_job_id: clientJobId },
                        15000
                    )
                    if (res.data && res.data.job_id) {
                        this.pendingJob = { job_id: res.data.job_id, backend_socket_id: res.data.backend_socket_id }
                        this.$socket.emit("joinChatJob", this.pendingJob.job_id)
                        this.chatWatchdogTimer = setTimeout(() => this.pollPendingChatJob(true), 330000)
                        return // loading/slowHint bleiben an, bis der Job fertig ist (siehe finishPendingChatJob)
                    }
                    // Uebergangs-Sicherheit: eine (noch) nicht aktualisierte API antwortet weiterhin synchron.
                    this.applyChatAnswer(res.data)
                } catch (e) {
                    let errorMsg = "Fehler beim Abrufen der Antwort."
                    if (e && e.code === "ECONNABORTED") {
                        errorMsg = "The analysis took too long and timed out. Try a narrower question or try again."
                    } else if (e && e.response && e.response.status === 504) {
                        errorMsg = "The connection to the server was interrupted. Please try again."
                    }
                    this.messages.push({
                        user: "KI",
                        text: errorMsg,
                        date: new Date()
                    })
                }
                clearTimeout(this.slowHintTimer)
                this.slowHint = false
                this.loading = false
                this.$nextTick(() => {
                    const el = this.$el.querySelector(".v-card-text")
                    if (el) el.scrollTop = el.scrollHeight
                })
            },
            generateClientJobId() {
                try {
                    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID()
                } catch (e) {}
                return "job_" + Date.now() + "_" + Math.random().toString(36).slice(2)
            },
            applyChatAnswer(data) {
                const mcpTables = []
                const mcpCalls = data?.messages?.filter((m) => m.type === "mcp_call") || []
                for (const mcp of mcpCalls) {
                    if (mcp.output) {
                        try {
                            const output = JSON.parse(mcp.output)
                            mcpTables.push({ name: mcp.name || "", rawOutput: output })
                        } catch (e) {}
                    }
                }
                this.messages.push({
                    user: "KI",
                    text: data?.messages?.[data.messages.length - 1]?.content?.[data.messages[data.messages.length - 1].content.length - 1]?.text || "Keine Antwort erhalten.",
                    date: new Date(),
                    tables: mcpTables
                })
            },
            onChatJobUpdate(data) {
                if (!this.pendingJob || !data || data.job_id !== this.pendingJob.job_id) return
                if (data.status === "done" || data.status === "error" || data.status === "cancelled") this.pollPendingChatJob()
            },
            async pollPendingChatJob(fromWatchdog = false) {
                if (!this.pendingJob) return
                const job = this.pendingJob
                try {
                    const res = await this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/v2/sources/chat-job", {
                        user_id: localStorage.id,
                        company_id: localStorage.company_id,
                        job_id: job.job_id
                    })
                    const view = res && res.data
                    if (!view || view.status === "queued" || view.status === "running") {
                        // Watchdog-Ablauf ohne Ergebnis: nicht endlos "generating response..." zeigen.
                        if (fromWatchdog) this.finishPendingChatJob(job, { status: "error", error: "lost" })
                        return
                    }
                    this.finishPendingChatJob(job, view)
                } catch (e) {
                    if (fromWatchdog) this.finishPendingChatJob(job, { status: "error", error: "lost" })
                }
            },
            finishPendingChatJob(job, view) {
                if (this.pendingJob !== job) return
                this.$socket.emit("leaveChatJob", job.job_id)
                this.pendingJob = null
                if (this.chatWatchdogTimer) clearTimeout(this.chatWatchdogTimer)
                this.chatWatchdogTimer = null
                clearTimeout(this.slowHintTimer)
                this.slowHint = false
                this.loading = false
                if (view.status === "done") {
                    this.applyChatAnswer(view.answer)
                } else if (view.status !== "cancelled") {
                    const errorMsg = view.error === "lost" ? "The connection to the analysis was lost. Please try again." : "Fehler beim Abrufen der Antwort."
                    this.messages.push({ user: "KI", text: errorMsg, date: new Date() })
                }
                this.$nextTick(() => {
                    const el = this.$el.querySelector(".v-card-text")
                    if (el) el.scrollTop = el.scrollHeight
                })
            },
            // Dummy for username, replace with your logic if needed
            getUsername() {
                try {
                    if (localStorage.username && this.CryptoJS && process.env.VUE_APP_ENCRYPTION_PW) {
                        return this.CryptoJS.AES.decrypt(localStorage.username, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                    }
                } catch (e) {}
                return "You"
            },
            formatDate(date) {
                if (!date) return ""
                const d = new Date(date)
                return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
            },
            resolveNested(item, col) {
                if (!col.includes(".")) return item[col]
                const [main, sub] = col.split(".")
                return item[main] && typeof item[main] === "object" ? item[main][sub] : ""
            },
            // For real-time: call this on socket event
            receive(msg) {
                this.messages.push(msg)
                if (!this.chatOpen) this.unreadCount++
            },
            async showTable(table) {
                const cacheKey = JSON.stringify(table.rawOutput)
                this.selectedTable = null
                this.tableDialog = false
                this.$set(this.loadingTable, cacheKey, true) // Nur diesen Button auf loading

                if (this.tableDefCache[cacheKey]) {
                    const table_def = this.tableDefCache[cacheKey]
                    const sourceField = table_def.source_field || "items"
                    let items = Array.isArray(table.rawOutput[sourceField]) ? table.rawOutput[sourceField] : []
                    this.selectedTable = {
                        name: table.name || "",
                        columns: table_def.columns,
                        items,
                        chart: table_def.chart
                    }
                    this.tableDialog = true
                    this.$set(this.loadingTable, cacheKey, false)
                    return
                }

                try {
                    const res = await this.$helpers.axiosPost(process.env.VUE_APP_APIURL + "/v2/sources/mcp/tabledef", { output: JSON.stringify(table.rawOutput) })
                    console.log(res.data.table_def)
                    const table_def = res.data.table_def
                    this.tableDefCache[cacheKey] = table_def
                    const sourceField = table_def.source_field || "items"
                    let items = Array.isArray(table.rawOutput[sourceField]) ? table.rawOutput[sourceField] : []
                    this.selectedTable = {
                        name: table.name || "",
                        columns: table_def.columns,
                        items,
                        chart: table_def.chart
                    }
                    this.tableDialog = true
                } catch (e) {
                    this.$toast && this.$toast.error("Fehler beim Laden der Tabelle.")
                }
                this.$set(this.loadingTable, cacheKey, false)
            },
            onTableDialogInput(val) {
                this.tableDialog = val
                if (!val) this.selectedTable = null
            }
        }
    }
</script>

<style scoped>
    .comment-area {
        margin-bottom: 10px;
        margin-top: 10px;
    }
    .comment-user {
        margin-left: 5px;
        font-weight: bold;
        font-size: 12px;
        padding-bottom: 6px;
    }
    .comment-date {
        margin-left: 5px;
        font-style: italic;
        font-size: 12px;
    }
    .comment-text {
        margin-top: 5px;
        background-color: #ecfef9;
        border-radius: 20px;
        color: #1f1f1f;
        display: inline-block;
        max-width: 100%;
        padding: 10px 12px;
        box-sizing: border-box;
        white-space: pre-wrap;
        position: relative;
        width: fit-content;
        font-size: 0.875rem;
        line-height: 1.25rem;
        word-break: break-word;
        word-wrap: break-word;
    }
    .justify-end {
        justify-content: flex-end;
    }
    .ki-message {
        align-items: flex-start;
        margin-right: auto;
    }
    .chat-row-left {
        flex-direction: row;
        justify-content: flex-start;
    }
    .chat-row-right {
        flex-direction: row-reverse;
        justify-content: flex-end;
    }
    .ki-message .comment-text {
        background-color: #f7fafc; /* sehr hell, fast weiß */
        color: #222;
        border-radius: 16px 16px 16px 4px;
    }
    .user-message .comment-text {
        background-color: #1976d2;
        color: #fff;
        border-radius: 16px 16px 4px 16px;
        margin-left: auto;
    }
    .user-message {
        align-items: flex-end;
        margin-left: auto;
    }
</style>
